import { create } from "zustand";
import { persist } from "zustand/middleware";

import {
  getOnboardingLastStep,
  onboardingSlugFromStep,
  resolveStoredOnboardingStep,
} from "@/lib/onboardingSteps";

// Onboarding UX state. Completion is authoritative from the server
// (tenants.onboarding_completed_at via /auth/me + POST /onboarding/complete).

const LAST_ONBOARDING_STEP = getOnboardingLastStep();

function completedAtMs(isoOrMs) {
  if (isoOrMs == null) return null;
  if (typeof isoOrMs === "number") return isoOrMs;
  const ms = Date.parse(isoOrMs);
  return Number.isFinite(ms) ? ms : null;
}

export const useOnboardingStore = create(
  persist(
    (set, get) => ({
      completed: false,
      completedAt: null,
      currentStep: 1,
      data: {
        briefingTime: "07:30",
        briefingMethods: { push: true, email: false, whatsapp: false },
      },
      welcomeBannerDismissed: false,
      tourOptInDismissed: false,
      setStep: (n) => set({ currentStep: Math.max(1, Math.min(LAST_ONBOARDING_STEP, n)) }),
      /** Optimistic local complete — prefer hydrateFromServer / markCompleted after API. */
      complete: () => set({
        completed: true,
        completedAt: Date.now(),
        welcomeBannerDismissed: false,
        tourOptInDismissed: false,
      }),
      markCompleted: (completedAt) => set({
        completed: true,
        completedAt: completedAtMs(completedAt) || Date.now(),
        welcomeBannerDismissed: false,
        tourOptInDismissed: false,
      }),
      relaunch: () => set({
        completed: false,
        completedAt: null,
        currentStep: 1,
        welcomeBannerDismissed: false,
        tourOptInDismissed: false,
      }),
      hydrateFromServer: (payload) => {
        if (!payload || typeof payload !== "object") return;
        const completed = Boolean(
          payload.onboarding_completed
          ?? payload.completed
          ?? payload.onboarding_completed_at,
        );
        const step = resolveStoredOnboardingStep(
          payload.onboarding_step ?? payload.currentStep,
        );
        set({
          completed,
          completedAt: completed
            ? (completedAtMs(payload.onboarding_completed_at) || get().completedAt || Date.now())
            : null,
          currentStep: step,
        });
      },
      dismissWelcomeBanner: () => set({ welcomeBannerDismissed: true }),
      dismissTourOptIn: () => set({ tourOptInDismissed: true }),
      setData: (patch) => set((s) => ({ data: { ...s.data, ...patch } })),
      currentStepSlug: () => onboardingSlugFromStep(get().currentStep),
    }),
    {
      name: "sellix.onboarding.v1",
      partialize: (s) => ({
        currentStep: s.currentStep,
        data: s.data,
        welcomeBannerDismissed: s.welcomeBannerDismissed,
        tourOptInDismissed: s.tourOptInDismissed,
        completed: s.completed,
        completedAt: s.completedAt,
      }),
    },
  ),
);
